import React, { useEffect, useState } from 'react';

import Button from '~components/Button';
import ClickableIcon from '~components/ClickableIcon';
import { Fields } from '~components/form-fields';
import Icon, { faPenToSquare, faTriangleExclamation } from '~components/icon';
import Modal from '~components/Modal';

import { formatSettingLabel } from '~libs/stringarly';

import { SahServiceItem, SahServiceWrap } from '~lookups/models';

import fieldFactory from '~support-at-home/forms/supplier-invoices/details';

import { useGetSahServiceHptsQuery } from '~lookups/actions/sahServiceHpts';
import { useGetSahServicesQuery } from '~lookups/actions/sahServices';

interface ComponentProps {
  row: any;
  wraps: SahServiceWrap[];
  items: SahServiceItem[];
  onChange?: (field: string, value: any) => void;
  readOnly?: boolean;
}

interface Details {
  detail_type: 'wrap' | 'item' | null;
  detail_id: string | null;
  detail_description: string | null;
  sah_hpt_id: string | null;
  sah_hpt_description: string | null;
  is_first_payment_item: 'yes' | 'no' | null;
  is_prescribed_item: 'yes' | 'no' | null;
  purchase_method: string | null;
  sah_wrap_unit_type: string | null;
}

const defaultDetails: Details = {
  detail_type: null,
  detail_id: null,
  detail_description: null,
  sah_hpt_id: null,
  sah_hpt_description: null,
  is_first_payment_item: null,
  is_prescribed_item: null,
  purchase_method: null,
  sah_wrap_unit_type: null,
};

const toBoolOrNull = (val: 'yes' | 'no' | null): boolean | null => {
  if (val === 'yes') return true;
  if (val === 'no') return false;
  return null;
};

const fromBool = (val: boolean | null | undefined): 'yes' | 'no' | null => {
  if (val === true) return 'yes';
  if (val === false) return 'no';
  return null;
};

const ServiceDetail: React.FC<ComponentProps> = ({ row, onChange, items, wraps, readOnly = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [values, setValues] = useState<Details>(defaultDetails);
  const {
    detail_type,
    detail_id,
    detail_description,
    sah_hpt_id,
    sah_hpt_description,
    is_first_payment_item,
    is_prescribed_item,
    purchase_method,
    sah_wrap_unit_type,
  } = values;

  const clearDetails = () => setValues(defaultDetails);

  // Load existing values when modal opens
  useEffect(() => {
    if (isOpen) {
      let detailType: 'wrap' | 'item' | null = null;
      if (row.sah_wrap_id !== null) {
        detailType = 'wrap';
      } else if (row.sah_item_id !== null) {
        detailType = 'item';
      }

      const existingDetails: Details = {
        detail_type: detailType,
        detail_id: row.sah_wrap_id ?? row.sah_item_id ?? null,
        detail_description: row.sah_detail_description ?? null,
        sah_hpt_id: row.sah_hpt_id ?? null,
        sah_hpt_description: row.sah_hpt_description ?? null,
        is_first_payment_item: fromBool(row.is_first_payment_item),
        is_prescribed_item: fromBool(row.is_prescribed_item),
        purchase_method: row.purchase_method ?? null,
        sah_wrap_unit_type: row.sah_wrap_unit_type ?? null,
      };
      setValues(existingDetails);
    }
  }, [isOpen, row]);

  const handleClose = () => {
    clearDetails();
    setIsOpen(false);
  };

  const handleSave = () => {
    if (!onChange || !detail_type || !detail_id) return;

    // Call onChange once with all values to avoid race conditions
    onChange?.('details', {
      sah_wrap_id: detail_type === 'wrap' ? detail_id : null,
      sah_item_id: detail_type === 'item' ? detail_id : null,
      sah_detail_description: isDetailOther ? detail_description : null,
      sah_hpt_id: sah_hpt_id || null,
      sah_hpt_description: sah_hpt_id && isHptOther ? sah_hpt_description : null,
      is_first_payment_item: firstPaymentRequired ? toBoolOrNull(is_first_payment_item) : null,
      is_prescribed_item: prescribedItemsAllowed ? toBoolOrNull(is_prescribed_item) : null,
      purchase_method: purchaseMethodAllowed ? purchase_method || null : null,
      sah_wrap_unit_type: detail_type === 'wrap' ? sah_wrap_unit_type || null : null,
    });

    setIsOpen(false);
    clearDetails();
  };

  // Get the selected item/wrap to find the service ID
  const selectedDetail =
    detail_type === 'wrap'
      ? wraps.find((wrap: any) => wrap.id === detail_id)
      : items.find((item: any) => item.id === detail_id);

  const serviceId = selectedDetail?.service_id || '';

  const { data: hptData = [], isLoading: isHptLoading, isFetching: isHptFetching } = useGetSahServiceHptsQuery();
  const {
    data: sahServices = [],
    isLoading: isServicesLoading,
    isFetching: isServicesFetching,
  } = useGetSahServicesQuery();

  // While the lookups are in flight, sahServices is [] so every requirement flag below
  // resolves false — which would let Save bypass the first-payment guard and rely on
  // backend rejection. Block Save (and show an indicator) until the metadata has loaded.
  const isLookupLoading = isHptLoading || isHptFetching || isServicesLoading || isServicesFetching;

  // Check meta_information.freeTextRequired instead of description
  const selectedHpt = hptData.find((hpt) => hpt.id === sah_hpt_id);
  const isHptOther = selectedHpt?.meta_information?.freeTextRequired === true;
  const isDetailOther = selectedDetail?.meta_information?.freeTextRequired === true;

  // Wraparound unit-type options come from the selected wrap's configured units.
  // Values stay lowercase to match meta_information; labels are title-cased.
  // meta_information is typed `any`, so filter to string entries before mapping —
  // a non-string unit would throw in formatSettingLabel during render.
  const wrapUnits = selectedDetail?.meta_information?.units;
  const wrapUnitTypeOptions =
    detail_type === 'wrap' && Array.isArray(wrapUnits)
      ? wrapUnits
          .filter((unit: unknown): unit is string => typeof unit === 'string')
          .map((unit) => ({
            id: unit,
            name: formatSettingLabel(unit),
          }))
      : [];

  // Check if the service requires first payment indicator
  const selectedService = sahServices.find((s) => s.id === serviceId);
  const firstPaymentRequired = selectedService?.first_payment_indicator_required === true;
  const prescribedItemsAllowed = selectedService?.prescribed_items_allowed === true;
  const purchaseMethodAllowed = selectedService?.purchase_method_allowed === true;

  // Prepare enhanced values for Fields component
  const enhancedValues = {
    ...values,
    isHptOther,
    isDetailOther,
    firstPaymentRequired,
    prescribedItemsAllowed,
    purchaseMethodAllowed,
    wrapUnitTypeOptions,
  };

  const shouldAlert = !row.sah_wrap_id && !row.sah_item_id && (items.length > 0 || wraps.length > 0);
  const hasOptions = items.length > 0 || wraps.length > 0;

  const setFieldValue = (field: string, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const { fields, itemOptions, wrapOptions } = fieldFactory(
    serviceId,
    wraps,
    items,
    hptData,
    readOnly,
  );

  // Dynamically set options for detail_id based on current detail_type
  const fieldsWithOptions = fields.map((field) => {
    if (field.key === 'detail_id') {
      return {
        ...field,
        options: detail_type === 'wrap' ? wrapOptions : itemOptions,
      };
    }
    if (field.key === 'sah_wrap_unit_type') {
      return {
        ...field,
        options: wrapUnitTypeOptions,
      };
    }
    return field;
  });

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        setIsOpen={setIsOpen}
        header={readOnly ? 'View Detail' : 'Add/Edit Detail'}
        size="lg"
        footer={
          <div className="d-flex gap-2">
            <Button
              color="danger"
              onClick={() => {
                clearDetails();
                setIsOpen(false);
              }}
            >
              {readOnly ? 'Close' : 'Cancel'}
            </Button>
            {!readOnly && (
              <Button
                type="submit"
                color="success"
                onClick={handleSave}
                disabled={
                  isLookupLoading ||
                  !detail_type ||
                  !detail_id ||
                  (firstPaymentRequired && is_first_payment_item === null)
                }
              >
                Save
              </Button>
            )}
          </div>
        }
      >
        <div className="d-flex flex-column gap-2 justify-content-center">
          {isLookupLoading && <div className="text-muted">Loading service metadata…</div>}
          <Fields
            definitions={fieldsWithOptions}
            values={enhancedValues}
            setFieldValue={setFieldValue}
            setFieldTouched={() => null}
          />
        </div>
      </Modal>
      {hasOptions && (
        <div className="d-flex align-items-center justify-content-center position-relative">
          <ClickableIcon id={`item-wraps-${row.id}`} icon={faPenToSquare} onClick={() => setIsOpen(true)} />
          {shouldAlert && <Icon icon={faTriangleExclamation} className="icon-alert" />}
        </div>
      )}
    </>
  );
};

export default ServiceDetail;
